import { Injectable, EventEmitter, Output, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { ThemeService } from './theme/theme.service';
import { MessageSeverity } from '../enums/message-severity';
import { BehaviorSubject, Observable } from 'rxjs';
import { DialogData } from '../interfaces/dialog-data';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogComponent } from '../components/dialog/dialog.component';
import { AppMessage } from '../interfaces/app-message';
import { AuthService } from './auth/auth.service';
import { AuthProvider } from '../enums/auth-provider';
import { UserSession } from '../interfaces/user-session';
import { NodeResult } from '../models/net/result/node-result';
import { ActionButton } from '../models/app/action-button';
import { ActingProfileTileComponent } from '../components/fragments/talent-core/acting-profile-tile/acting-profile-tile.component';
import { ComponentDialogComponent } from '../components/component-dialog/component-dialog.component';
import { ComponentType } from '@angular/cdk/portal';
import { ItemEnvelope } from '../models/net/envelope/base/item-envelope';
import { ComponentDialogResult } from '../models/app/component-dialog-result';
import { PersonService } from './persons/person.service';
import { SearchPreference } from '../models/data/app-core/search-preference';
import { ReadResult } from '../models/net/result/crud/read-result';
import { PersonDocument } from '../models/net/envelope/documents/person-document';
import { PersonEnvelope } from '../models/net/envelope/person-envelope';
import { SearchRequestTypeEnum } from '../enums/search-request-type-enum';
import { Session } from '../models/data/app-core/session';
import { SessionDocument } from '../models/net/envelope/documents/session-document';
import { environment } from '../../environments/environment';
import { UtilityFunctions } from '../utilities/utility-functions';
import { ScreenSize } from '../enums/screen-size';
import { Role } from '../models/data/identity/role';
import { SubscriberComponent } from '../components/fragments/subscriber/subscriber.component';

@Injectable({
    providedIn: 'root'
})

export class AppService {

    private _apiBaseUrl: string = "";
    private _isAppBusy: boolean = false;
    private _userSessionName: string = "userSession";

    public appName: string = "LutzAngeles"
    public currentUserSession: any = null;
    public screenSize: ScreenSize = ScreenSize.Unknown;
    public isNavigationCollapsed: boolean = true;
    public isHelpPaneCollapsed: boolean = true;
    public isAppFeedPaneCollapsed: boolean = true;
    public isShortcutsPaneAvailable: boolean = false;
    public isShareConsoleAvailable: boolean = false;
    public shortcutsPaneDynamicClassName: string = "shortcuts-pane-expanded";
    public navigationPaneDynamicClassName: string = "navigation-pane-collapsed";
    public mobilenavigatorPaneDynamicClassName: string = "mobilenavigator-pane-collapsed"
    public headerTopDynamicClassName: string = "header-top-nav-collapsed";
    public breadcrumbsDynamicClassName: string = "breadcrumbs-container-nav-collapsed";
    public shareConsoleDynamicClassName: string = "share-console-collapsed";
    public appFeedPaneDynamicClassName: string = "appfeed-pane-collapsed";
    public appFeedButtonDynamicClassName: string = "appfeed-button-pane-collapsed";
    public appFeedMatFormFieldContainerDynamicClassName: string = "appfeed-matformfield-container-collapsed";
    public isNavigationPinned: boolean = true;
    public isLoggedIn: boolean = false;
    public loggedSession: any = null;
    public defaultImageUri: string = "/assets/tst-pattern.jpg";
    public isAppBusySubject: BehaviorSubject<boolean> = new BehaviorSubject<any>(undefined);
    public appFeedMessages: AppMessage[] = [];
    public actionsDatasource: ActionButton[] = [];
    public isRootNodesCountAboveFooterCapacity: boolean = false;

    public APPFEEDPANE_AUTOCOLLAPSE_MS_DEFAULT: number = 3000;

    public get IsAppBusy(): boolean {

        return this._isAppBusy;
    }

    public get registeredDomain(): string {

        return environment.registeredDomain;
    }

    public set IsAppBusy(value: boolean) {

        let previousValue: boolean = this._isAppBusy;

        this._isAppBusy = value;

        if (this._isAppBusy != previousValue) {

            this.isAppBusySubject.next(value);
        }
    }

    @Output() screenSizeChange = new EventEmitter<ScreenSize>();
    @Output() loggedIn = new EventEmitter<any>();
    @Output() loggedOut = new EventEmitter<any>();
    @Output() navigatedToRoute = new EventEmitter<any>();
    @Output() actionsDataSourceChanged = new EventEmitter<any>();
    @Output() actionButtonClicked = new EventEmitter<string>();
    @Output() appFeedPaneToggled = new EventEmitter<boolean>();
    @Output() appThemeChanged = new EventEmitter<string>();

    public constructor(public authService: AuthService,
                       public router: Router,
                       public personService: PersonService, 
                       public themeService: ThemeService,
                       public matDialog: MatDialog, 
                       @Inject("API_BASE_URL") apiBaseUrl: string) {

        this._apiBaseUrl = apiBaseUrl;

        this.appFeedMessages = this.getAppFeedMessages();

        this.screenSizeChange.subscribe((screenSize: ScreenSize) => {

            if (screenSize === ScreenSize.Mobile) {

                this.isShortcutsPaneAvailable = false;
                this.isShareConsoleAvailable = false;

                this.toggleShareConsoleClosed();
                this.toggleNavigationClosed();
                this.toggleShortcutsPaneClosed();
                this.toggleMobileNavigatorPaneOpen();

            } else if (screenSize === ScreenSize.Tablet) {

                this.isShortcutsPaneAvailable = true;
                this.isShareConsoleAvailable = true;

                this.toggleShareConsoleClosed();
                this.toggleNavigationClosed();
                this.toggleShortcutsPaneOpen();
                this.toggleMobileNavigatorPaneClosed();

            } else if (screenSize === ScreenSize.Desktop) {

                this.isShortcutsPaneAvailable = true;
                this.isShareConsoleAvailable = true;

                this.toggleShareConsoleOpen();
                this.toggleNavigationOpen();
                this.toggleShortcutsPaneClosed();
                this.toggleMobileNavigatorPaneClosed();
            }
        });
    }

    public storeLoggedUser = (user: any): any => {

        const sessionGuid = uuidv4();
        const userSession: any = { SessionId: sessionGuid, AuthenticatedUser: user };

        localStorage.setItem(this._userSessionName, JSON.stringify(userSession));
        this.currentUserSession = userSession;

        return userSession;
    }

    public isMemberOfAdminRole = (): boolean => {

        var result = this.loggedSession?.serverSession?.Roles?.some((r: Role) => r.RoleId === 1);

        return result;
    }

    public navigateTo = (destination: string, subdomain: string): void => {

        let fullReloadUrl: string = "";

        if (!destination.startsWith('http')) {

            if (subdomain != "") {

                const currentUrl = window.location.href;
                const url = new URL(currentUrl);
                const hostParts = url.hostname.split('.');
                let currentSubdomain: string = "";

                if (hostParts.length > 1) {

                    currentSubdomain = hostParts[0];

                    if (currentSubdomain != subdomain) {

                        hostParts[0] = subdomain; // Replace subdomain

                        url.hostname = hostParts.join('.');
                        url.pathname = destination

                        if (this.themeService.currentThemeName == "dark") {

                            if (!url.pathname.endsWith('at/night')) {

                                url.pathname = url.pathname.replace(/\/$/, '') + 'at/night';
                            }
                        }

                        window.location.replace(url.toString());

                    } else {

                        this.router.navigate([destination]);
                        this.navigatedToRoute.emit(destination);
                    }
                }

            } else {

                this.router.navigate([destination]);
                this.navigatedToRoute.emit(destination);
            }

        } else {

            window.open(destination, '_blank');
        }

        if (this.screenSize != ScreenSize.Desktop) {

            this.toggleNavigationClosed();
        }
    }

    public toggleNavigationOpen = (): void => {

        this.isNavigationCollapsed = true;

        this.toggleNavigation();
    }

    public toggleNavigationClosed = (): void => {

        this.isNavigationCollapsed = false;

        this.toggleNavigation();
    }

    public toggleNavigation = (): void => {

        if (this.isNavigationCollapsed) {

            this.shortcutsPaneDynamicClassName = "shortcuts-pane-collapsed";
            this.navigationPaneDynamicClassName = "navigation-pane-expanded";
            this.headerTopDynamicClassName = "header-top-nav-expanded";
            this.breadcrumbsDynamicClassName = "breadcrumbs-container-nav-expanded";
            this.isNavigationCollapsed = false;

        } else {

            //this.shortcutsPaneDynamicClassName = "shortcuts-pane-expanded";
            if (this.isShortcutsPaneAvailable) {
                setTimeout(() => { this.shortcutsPaneDynamicClassName = "shortcuts-pane-expanded"; }, 500);
            }
            this.navigationPaneDynamicClassName = "navigation-pane-collapsed";
            this.headerTopDynamicClassName = "header-top-nav-collapsed";
            this.breadcrumbsDynamicClassName = "breadcrumbs-container-nav-collapsed";
            this.isNavigationCollapsed = true;
        }
    }

    public toggleShareConsoleOpen = (): void => {

        this.isHelpPaneCollapsed = true;

        this.toggleHelpPane();
    }

    public toggleShareConsoleClosed = (): void => {

        this.isHelpPaneCollapsed = false;

        this.toggleHelpPane();
    }

    public toggleShortcutsPaneOpen = (): void => {

        setTimeout(() => { this.shortcutsPaneDynamicClassName = "shortcuts-pane-expanded"; }, 500);        
    }

    public toggleShortcutsPaneClosed = (): void => {

        this.shortcutsPaneDynamicClassName = "shortcuts-pane-collapsed";
    }

    public toggleMobileNavigatorPaneOpen = (): void => {

        this.mobilenavigatorPaneDynamicClassName = "mobilenavigator-pane-expanded";
    }

    public toggleMobileNavigatorPaneClosed = (): void => {

        this.mobilenavigatorPaneDynamicClassName = "mobilenavigator-pane-collapsed";
    }

    public toggleHelpPane = (): void => {

        if (this.isHelpPaneCollapsed) {

            this.shareConsoleDynamicClassName = "share-console-expanded";
            this.isHelpPaneCollapsed = false;

        } else {

            this.shareConsoleDynamicClassName = "share-console-collapsed";
            this.isHelpPaneCollapsed = true;
        }
    }

    public toggleappFeedPane = (): void => {

        if (this.isAppFeedPaneCollapsed) {

            this.appFeedMatFormFieldContainerDynamicClassName = "appfeed-matformfield-container-expanded";

            setTimeout(() => {

                this.appFeedPaneDynamicClassName = "appfeed-pane-expanded";
                this.appFeedButtonDynamicClassName = "appfeed-button-pane-expanded";
                this.isAppFeedPaneCollapsed = false;

            }, 1); // for whatever reason, there is no slide open effect without this brief delay

            this.appFeedPaneToggled.emit(true);

        } else {

            this.appFeedPaneDynamicClassName = "appfeed-pane-collapsed";
            this.appFeedButtonDynamicClassName = "appfeed-button-pane-collapsed";
            this.isAppFeedPaneCollapsed = true;

            setTimeout(() => {

                this.appFeedMatFormFieldContainerDynamicClassName = "appfeed-matformfield-container-collapsed";

            }, 250);

            this.appFeedPaneToggled.emit(false);
        }
    }

    public openAppFeedPane = (closeAfterMilliseconds: number = 0): void => {

        if (this.isAppFeedPaneCollapsed) {

            this.appFeedPaneDynamicClassName = "appfeed-pane-expanded";
            this.appFeedButtonDynamicClassName = "appfeed-button-pane-expanded";
            this.isAppFeedPaneCollapsed = false;
        }

        if (closeAfterMilliseconds > 0) {

            setTimeout(() => {

                this.appFeedPaneDynamicClassName = "appfeed-pane-collapsed";
                this.appFeedButtonDynamicClassName = "appfeed-button-pane-collapsed";
                this.isAppFeedPaneCollapsed = true;

                setTimeout(() => {

                    this.appFeedMatFormFieldContainerDynamicClassName = "appfeed-matformfield-container-collapsed";

                }, 250);

            }, closeAfterMilliseconds);
        }
    }

    public closeAppFeedPane = (): void => {

        if (!this.isAppFeedPaneCollapsed) {

            this.appFeedPaneDynamicClassName = "appfeed-pane-collapsed";
            this.appFeedButtonDynamicClassName = "appfeed-button-pane-collapsed";
            this.isAppFeedPaneCollapsed = true;
        }
    }

    public toggleTheme = (themeName: string) => {

        if (themeName != "") {

            this.themeService.loadTheme(themeName);
            this.appThemeChanged.emit(themeName);

        } else {

            let toggledThemeName = this.themeService.currentThemeName == "dark" ? "light" : "dark";

            this.themeService.loadTheme(toggledThemeName);
            this.appThemeChanged.emit(themeName);
        }
    }

    public showDialog = (severity: MessageSeverity,
                         title: string,
                         message: string,
                         acceptButtonText: string,
                         cancelButtonText: string,
                         isIncludeAppFeed: boolean = false, 
                         isShowIncludedAppFeedPane: boolean = false,
                         isHeaderEditable: boolean = false): Observable<string> => {

        let result: BehaviorSubject<string> = new BehaviorSubject<any>(undefined);

        const acceptButtonCaption = acceptButtonText ?? "";
        const cancelButtonCaption = cancelButtonText ?? "";

        let dialogData: DialogData = {
            Severity: severity,
            MaterialIconName: "",
            Title: title,
            Subtitle: "",
            Message: message,
            AcceptButtonText: acceptButtonCaption,
            CancelButtonText: cancelButtonCaption,
            IsHeaderEditable: isHeaderEditable
        };

        const dialogConfiguration = new MatDialogConfig();

        dialogConfiguration.disableClose = false;
        dialogConfiguration.autoFocus = true;
        dialogConfiguration.data = dialogData;

        const dialog = this.matDialog.open(DialogComponent, dialogConfiguration);

        if (isIncludeAppFeed) {

            this.addAppFeedMessage(severity, message, isShowIncludedAppFeedPane);
        }

        dialog.afterClosed().subscribe((response) => {

            result.next(response);
            result.complete();
        });

        return result;
    }

    //public showComponentDialog(): void {

    //    let dialogRef = this.matDialog.open(ActingProfileTileComponent, { width: '450px', data: { component: ActingProfileTileComponent } });
    //}

    public showComponentDialog = <TComponent>(componentType: ComponentType<TComponent>,
                                                itemEnvelope: any,
                                                title: string,
                                                subtitle: string,
                                                materialIconName: string,
                                                acceptButtonText: string = "OK",
                                                closeButtonCaption: string = "CLOSE",
                                                isHeaderEditable: boolean = false,
                                                isAutoFocus: boolean = true): Observable<ComponentDialogResult | null> => {

        let result: BehaviorSubject<ComponentDialogResult | null> = new BehaviorSubject<ComponentDialogResult | null>(null);

        let dialogData: DialogData = {
            Severity: MessageSeverity.Info,
            MaterialIconName: materialIconName,
            Title: title,
            Subtitle: subtitle, 
            Message: "", 
            AcceptButtonText: "OK", 
            CancelButtonText: "CANCEL",
            IsHeaderEditable: isHeaderEditable
        };

        const dialogConfiguration = new MatDialogConfig();

        dialogConfiguration.disableClose = false;
        dialogConfiguration.autoFocus = isAutoFocus;
        dialogConfiguration.data = dialogData;

        const dialog = this.matDialog.open(ComponentDialogComponent, dialogConfiguration);

        dialog.componentInstance.acceptButtonCaption = acceptButtonText;
        dialog.componentInstance.closeButtonCaption = closeButtonCaption;
        dialog.componentInstance.loadComponent(componentType, itemEnvelope);

        dialog.afterClosed().subscribe((response: ComponentDialogResult) => {

            result.next(response);
            result.complete();
        });

        return result;
    }

    public showPendingFeatureDialog = (): void => {

        this.showDialog(MessageSeverity.Info, "Pending Feature", "Pending feature ! not implemented :(", "OK", "", false, false);
    }

    public addAppFeedMessage = (severity: MessageSeverity, message: string, isShowAppfeedPane: boolean): void => {

        const newMessage: AppMessage = {
            MessageDateTime: new Date,
            MessageSeverity: severity,
            MessageText: message
        };

        this.appFeedMessages.unshift(newMessage);

        if (isShowAppfeedPane) {

            this.openAppFeedPane(this.APPFEEDPANE_AUTOCOLLAPSE_MS_DEFAULT);
        }
    }

    public createUserSession = (userId: number, userUID: string, identityProviderUID: string, avatarUri: string, bearerToken: string, authProvider: AuthProvider): UserSession => {

        const domainParts = window.location.hostname.split('.');
        const domain = domainParts.slice(-2).join('.'); // extract the subdomain so that the cookie is visible to subdomains as opposed to only www
        const maxAgeSeconds: number = 3600;
        const expiresDate = new Date();

        expiresDate.setFullYear(expiresDate.getFullYear() + 1000);

        const result: UserSession = {
            sessionId: uuidv4(),
            bearerToken: bearerToken,
            authProvider: authProvider,
            userId: userId,
            userUID: userUID,
            identityProviderUserUID: identityProviderUID,
            avatarUri: avatarUri,
            serverSession: new SessionDocument()
        };

        //localStorage.setItem(this._userSessionName, JSON.stringify(result));

        const sessionData = encodeURIComponent(JSON.stringify(result));
        let cookieData = "userSession=" + sessionData + "; path=/; domain=" + domain + "; expires=" + expiresDate.toUTCString() + "; Secure; SameSite=None";

        document.cookie = cookieData;

        return result;
    }

    public getUserSession = (): string => {

        let result: string = UtilityFunctions.getUserSession();

        return result;
    }

    public getAppFeedMessages = (): AppMessage[] => {

        const result: AppMessage[] = [{ MessageDateTime: new Date(), MessageSeverity: MessageSeverity.Info, MessageText: "You can collapse this window anytime you like and open it again to see messages from the app." },
                                      { MessageDateTime: new Date(), MessageSeverity: MessageSeverity.Info, MessageText: "Hello, welcome !  The app is online :)" },
                                      { MessageDateTime: new Date(), MessageSeverity: MessageSeverity.Info, MessageText: "This is the app feed.  Application is loading..." }];

        return result;
    }

    public setActionsDataSource = (actionsArray: ActionButton[]): void => {

        this.actionsDatasource = actionsArray;
        this.actionsDataSourceChanged.emit(this.actionsDatasource);
    }

    public raiseActionButtonClickedEvent = (actionName: string): void => {

        this.actionButtonClicked.emit(actionName);
    }

    public cloneObject = (o: any): any => {

        const result: any = JSON.parse(JSON.stringify(o));

        return result;
    }

    public getSearchPreference = (searchRequestType: SearchRequestTypeEnum): Observable<SearchPreference | null> => {

        let result: BehaviorSubject<SearchPreference | null> = new BehaviorSubject<SearchPreference | null>(null);
        const emptySearchPreference: SearchPreference = new SearchPreference();

        const userSessionString: string | null = this.getUserSession();

        if (userSessionString) {

                const userSession: UserSession = JSON.parse(userSessionString);

                this.personService.readByUserUIDSessionId(userSession.userUID, "000000000000-0000-0000-0000-00000000").subscribe((r: ReadResult | null) => {

                    if (r) {

                        if (r.IsSuccess) {

                            const currentPersonDocument: PersonDocument = (r.ReadEnvelope as PersonEnvelope).Document;
                            const itemTypeId: number = searchRequestType;

                            const searchPreference: SearchPreference | undefined = currentPersonDocument.SearchPreferences.find((i: SearchPreference) => i.SearchRequestTypeId === itemTypeId);

                            if (searchPreference) {

                                result.next(searchPreference);
                                result.complete();

                            } else {

                                result.next(emptySearchPreference);
                                result.complete();
                            }

                        } else {

                            result.next(emptySearchPreference);
                            result.complete();
                        }
                    }
                });
        }

        return result;
    }

    public isValidOrganizationalUnitName = (ouName: string): boolean => {

        var result = false;

        if (ouName.toLowerCase() == "mendls") {

            result = true;
        }

        return result;
    }

    public showSubscribeDialog = (): void => {

        this.showComponentDialog(SubscriberComponent, null, "Subscribe", "Lutz Angeles Newz + Mendl's Events", "Edit", "", "CLOSE", false, false).subscribe((r: ComponentDialogResult | null) => {

            if (r) {

                if (r.IsAccepted) {

                    // this.showPendingFeatureDialog();
                }
            }
        });
    }

    public composeEmail = (to: string, subject: string, bodyTemplate: string): void => {

        const acceptButtonText: string = "CREATE ORDER EMAIL";

        this.showDialog(MessageSeverity.Info, "Order Proceure", "Because Mendl's is a small operation, you'll need to send an order request by email directly to Mendls.  Please fillout the email we will compose for you with quantity and desired delivery date.  Mendl's will respond asap", acceptButtonText, "CANCEL", false, false, false).subscribe((response: string) => {

            if (response == acceptButtonText) {

                const mailtoLink = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyTemplate)}`;
                window.location.href = mailtoLink;
            }
        });
    }

    public goToSubdomain = (subdomain: string): void => {

        const currentUrl = window.location.href;
        const url = new URL(currentUrl);
        const hostParts = url.hostname.split('.');

        if (hostParts.length > 1) {

            hostParts[0] = subdomain; // Replace subdomain

        } else {

            hostParts.unshift(subdomain); // If no subdomain, add one
        }

        url.hostname = hostParts.join('.');

        if (this.themeService.currentThemeName == "dark") {

            if (!url.pathname.endsWith('at/night')) {

                url.pathname = url.pathname.replace(/\/$/, '') + 'at/night';
            }
        }

        window.location.replace(url.toString());
    }

    public async share(title: string = "", text: string = ""): Promise<void> {

        if (title == "") {
            title = "Lutz Angeles";
        }

        if (text == "") {
            text = "Home of Mendl's";
        }

        const shareData = {
            title: title, 
            text: text, 
            url: window.location.href
        };

        if (navigator.share) {

            try {

                await navigator.share(shareData);

            } catch (e) {
                //
            }

        } else {

            await navigator.clipboard.writeText(shareData.url);

            this.showDialog(MessageSeverity.Success, "Share", "Link copied to clipboard !", "", "CLOSE", false, false, false);
        }
    }

    public loggedInGoogle = (token: string, userId: number, userUID: string, identityProviderUserUID: string, profileImageUrl: string): UserSession | undefined => {

        var result: UserSession | undefined = undefined;
        //const token: any = credential.credential;
        let base64Url = token.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        //let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) { return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2); }).join(''));
        //let user: any = JSON.parse(jsonPayload)

        result = this.createUserSession(userId, userUID, identityProviderUserUID, profileImageUrl, token, AuthProvider.Google);

        //this.loggedSession = result;
        //this.isLoggedIn = true;

        //this.loggedIn.emit(result);

        return result;
    }

    public loggedInInstagram = (token: string, userId: number, userUID: string, identityProviderUserUID: string, profileImageUrl: string): UserSession | undefined => {

        var result: UserSession | undefined = this.createUserSession(userId, userUID, identityProviderUserUID, profileImageUrl, token, AuthProvider.Instagram);

        //this.loggedSession = result;
        //this.isLoggedIn = true;

        //this.loggedIn.emit(result);

        return result;
    }

    public loggedInApp = (userSession: UserSession): void => {

        this.loggedSession = userSession;
        this.isLoggedIn = true;

        //this.toggleNavigationOpen();
        //this.toggleShareConsoleOpen();

        //localStorage.setItem(this._userSessionName, JSON.stringify(userSession));

        this.loggedIn.emit(userSession);
    }

    public logout = () => {

        const domainParts = window.location.hostname.split('.');
        const domain = domainParts.slice(-2).join('.'); // extract the subdomain so that the cookie is visible to subdomains as opposed to only www

        localStorage.removeItem(this._userSessionName);
        document.cookie = "userSession=; path=/; domain=" + domain + "; expires=Thu, 01 Jan 1970 00:00:00 UTC"; // remove the cookie with a past expiration date 

        this.isLoggedIn = false;

        this.addAppFeedMessage(MessageSeverity.Info, "You have logged out - redirecting to login", true);

        this.loggedOut.emit(null);
        
        this.isLoggedIn = false;

        this.goToSubdomain("www");
    }
}
